import { Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch, Table, TableCell, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import CopyButton from "~/components/common/CopyButton";
import DownloadButton from "~/components/common/DownloadButton";
import { CreateApiKeyMutation } from "~/graphql/__generated__/graphql";

type Props = {
  createdKey: CreateApiKeyMutation["createApiKey"]
  onClose: () => void
}

const KeyCreatedDialog = ({ createdKey, onClose }: Props) => {
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [usePemFormat, setUsePemFormat] = useState(true);

  const isSftp = createdKey.apiKey.keyPurpose === "sftp";
  // SFTP keys are always provided as PEM; the DER/PEM toggle only applies to endpoint keys.
  const isPem = isSftp || usePemFormat;

  const keyText = isPem ? createdKey.privatePem : createdKey.privateDer;
  const rows = keyText.split("\n").length

  const identifierLabel = isSftp ? "Username" : "API Key";
  const secretLabel = isSftp ? "SSH Private Key" : "API Key Secret";

  const downloadPemKey = async () => ({
    fileType: "application/x-pem-file",
    fileName: `medarrive-${isSftp ? "sftp" : "api"}-${createdKey.apiKey.apiKey}.pem`,
    text: createdKey.privatePem,
  });

  return <Dialog open={true} onClose={onClose} maxWidth={false}>
    <DialogTitle>{isSftp ? "New SFTP Key" : "New API Key"}</DialogTitle>
    <DialogContent>
      <Table size="small">
        <TableRow>
          <TableCell sx={{ borderBottom: "none" }}><Typography>{identifierLabel}<CopyButton text={createdKey.apiKey.apiKey} /></Typography></TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Typography data-testid="new-api-key">{createdKey.apiKey.apiKey}</Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ borderBottom: "none" }}>
            <Typography>
              {secretLabel}
              <CopyButton text={keyText} />
              {/* Always reserve the download icon's space so the column width
                  (and therefore the second column) doesn't shift when toggling PEM. */}
              <Box component="span" sx={{ visibility: isPem ? "visible" : "hidden" }}>
                <Tooltip title="Download key as PEM file">
                  <DownloadButton fetch={downloadPemKey} />
                </Tooltip>
              </Box>
            </Typography>
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            {!showSecretKey && <Stack direction="row">
              <Stack direction="column">
                <Typography>**************************************</Typography>
              </Stack>
              <Button onClick={() => setShowSecretKey(!showSecretKey)}>{showSecretKey ? "Hide" : "Show"}</Button>
            </Stack>}
          </TableCell>
        </TableRow>
      </Table>
      {showSecretKey && <TextField
        multiline
        rows={rows}
        label="Private Key"
        value={keyText}
        contentEditable={false}
        fullWidth
        onFocus={(event) => {
          const field = event.target as HTMLInputElement;
          field.select();
        }}
        sx={{mt:2, mb:1, wordWrap: "normal" }}
      />}
      {!isSftp && <Stack direction="row" justifyContent={"space-between"}>
        <FormControlLabel control={<Switch checked={usePemFormat} onChange={() => setUsePemFormat(!usePemFormat)} />} label="Use PEM format" />
        <Stack direction="row" justifyContent={"flex-end"}>
          {showSecretKey && <Button sx={{right:0}} onClick={() => setShowSecretKey(!showSecretKey)}>{showSecretKey ? "Hide" : "Show"}</Button>}
        </Stack>
      </Stack>}
      <Alert severity="warning" sx={{mt: 2}}>
        <AlertTitle>
          <Typography>
            The secret cannot be retrieved once you leave this screen. Please save it in a secure location.
          </Typography>
        </AlertTitle>
      </Alert>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
}

export default KeyCreatedDialog
